/**
 * App-neutral links (I-2915). A notification — and each of its actions —
 * names what it is about as
 *
 *   <module key>/<resource type>/<resource id>[?query]
 *
 * ("mail/email/<uuid>?mailbox=<uuid>", "chat/channel/5?message=91"),
 * never as a route of one client: the web shell, the desktop apps and the
 * mobile app each open the resource their own way. In the web shell the app
 * that owns the module says where a resource lives: its `@<app>/connect`
 * module maps each resource type to a route path (`resourceLinks`), and the
 * shell resolves a link by its first segment — the module key, which is the
 * app's key.
 *
 * A link with a leading `/` is a web path of an older producer and no
 * resource link.
 *
 * Pure functions, no Vue.
 */

/** One resource of an app, as an app-neutral link names it. */
export interface ConnectResourceLink {
  /** The link's first segment: the module key, which is the app's key (`mail`). */
  module: string;
  /** The kind of resource (`email`, `channel`, `event`). */
  resourceType: string;
  /** The resource's id as the module wrote it, decoded (`5`, a uuid). */
  resourceID: string;
  /** The link's query parameters; of a repeated one the first value. */
  query: Readonly<Record<string, string>>;
  /**
   * The master company the resource belongs to, when the caller knows it (a
   * notification's `companyID`); unset or 0: none or unknown — mail, calendar
   * and chat belong to the account and name none. An app whose resources
   * live per company opens the resource in that company.
   */
  companyID?: number;
}

/**
 * Where a resource of the app is reached in the shell: a route path below the
 * app's base path (`/mail/mailboxes/<id>?email=<id>`). Null when the app
 * cannot open this one — nothing opens then.
 */
export type ConnectResourceLinkTarget = (link: ConnectResourceLink) => string | null;

/** An app's resource types and where each is reached (`ConnectAppModule.resourceLinks`). */
export type ConnectResourceLinks = Readonly<Record<string, ConnectResourceLinkTarget>>;

/** A module key or a resource type: lower snake case, as master checks them. */
const NAME_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

function decode(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

/**
 * Reads an app-neutral link. Null for anything else: a web path (leading
 * `/`), a URL with a scheme or a host, fewer or more than three segments, a
 * module key or resource type that is no lower-snake-case name, an empty id.
 */
export function parseConnectResourceLink(link: string): ConnectResourceLink | null {
  const raw = (link ?? '').trim();
  if (!raw || raw.startsWith('/') || raw.includes('://') || /[\\\r\n\t]/.test(raw)) return null;
  const at = raw.indexOf('?');
  const path = at < 0 ? raw : raw.slice(0, at);
  const segments = path.split('/');
  if (segments.length !== 3) return null;
  const [module, resourceType, rawID] = segments;
  if (!NAME_PATTERN.test(module) || !NAME_PATTERN.test(resourceType)) return null;
  const resourceID = decode(rawID);
  if (!resourceID) return null;
  const query: Record<string, string> = {};
  if (at >= 0) {
    for (const [key, value] of new URLSearchParams(raw.slice(at + 1))) {
      if (!(key in query)) query[key] = value;
    }
  }
  return { module, resourceType, resourceID, query };
}

/**
 * The route path an app's declaration gives a link; null when the app
 * declares no such resource type, cannot open this resource, or answers
 * anything but a path of the shell.
 */
export function resolveConnectResourceLink(
  links: ConnectResourceLinks | undefined,
  link: ConnectResourceLink
): string | null {
  if (!links || !Object.prototype.hasOwnProperty.call(links, link.resourceType)) return null;
  let target: string | null;
  try {
    target = links[link.resourceType](link);
  } catch {
    return null;
  }
  if (typeof target !== 'string' || !target.startsWith('/') || target.startsWith('//')) return null;
  return target;
}

/** A route path with its query: empty values are left out, the rest encoded. */
export function connectResourcePath(
  path: string,
  query: Readonly<Record<string, string | undefined>> = {}
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value) params.set(key, value);
  }
  const search = params.toString();
  return search ? `${path}?${search}` : path;
}
